/**
 * Navegação que é LINK de verdade (2026-10-26).
 *
 * Quase toda navegação das telas era `<button onClick={() => navigate(…)}>`,
 * e botão não tem clique do meio, Ctrl+clique nem "abrir em nova aba" no
 * menu do botão direito — a barra lateral, que já usava `<Link>`, tinha.
 * Este componente é o `Link` do react-router com as duas coisas que as
 * telas pedem em cima:
 *
 * - `stopPropagation`: DENTRO de uma linha ou card que tem `onClick`
 *   próprio, um Ctrl+clique abriria a aba nova E navegaria a atual pelo
 *   `onClick` do contêiner. Parar a propagação do clique do link resolve
 *   os dois lados.
 * - `onPlainClick`: o clique simples faz outra coisa que não navegar (abre
 *   o relatório na própria página, marca como lido) e o clique modificado
 *   ou do meio segue o padrão do navegador e abre o `to` numa aba nova.
 *   Sem isso o link só vale quando o destino é uma rota.
 *
 * Usa as MESMAS classes dos botões que substitui (`primary-button`,
 * `link-button`…) e acrescenta `app-link`, a marca que a folha de estilo
 * usa pra desfazer o que o navegador dá a um link e o botão não tem
 * (`base.css`).
 */

import type { MouseEvent } from "react";
import { Link, type LinkProps } from "react-router-dom";

type AppLinkProps = Omit<LinkProps, "to" | "onClick" | "children"> & {
  to: string;
  children?: LinkProps["children"];
  /** Sempre para a propagação do clique (ver o cabeçalho). */
  stopPropagation?: boolean;
  /** Clique simples (botão esquerdo, sem modificador) chama isto em vez de
   *  navegar; clique modificado ou do meio abre `to` numa aba nova. */
  onPlainClick?: () => void;
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
};

const isPlainClick = (event: MouseEvent<HTMLAnchorElement>) =>
  event.button === 0 &&
  !event.ctrlKey &&
  !event.metaKey &&
  !event.shiftKey &&
  !event.altKey;

export function AppLink({ stopPropagation, onPlainClick, onClick, className, ...rest }: AppLinkProps) {
  return (
    <Link
      {...rest}
      className={className ? `app-link ${className}` : "app-link"}
      onClick={(event) => {
        if (stopPropagation) event.stopPropagation();
        onClick?.(event);
        if (onPlainClick && isPlainClick(event)) {
          event.preventDefault();
          onPlainClick();
        }
      }}
    />
  );
}
